import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Image, Upload, X, Loader2, History, ZoomIn } from 'lucide-react';
import { SiteSketchVersionHistory } from './SiteSketchVersionHistory';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface SiteSketchUploaderProps {
  locationId: string;
  currentUrl: string | null;
  onUpdate: (url: string | null) => void;
  className?: string;
}

export function SiteSketchUploader({ locationId, currentUrl, onUpdate, className }: SiteSketchUploaderProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const handleUpload = async (file: File) => {
    // Validate file type
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      toast({
        title: 'Ongeldig bestandstype',
        description: 'Alleen PNG, JPEG en WebP zijn toegestaan',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'Bestand te groot',
        description: 'Maximum bestandsgrootte is 10MB',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Generate unique filename
      const ext = file.name.split('.').pop() || 'jpg';
      const fileName = `${locationId}/${Date.now()}.${ext}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('location-sketches')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('location-sketches')
        .getPublicUrl(fileName);

      // Get current max version number
      const { data: existingVersions } = await supabase
        .from('site_sketch_versions')
        .select('version_number')
        .eq('location_id', locationId)
        .order('version_number', { ascending: false })
        .limit(1);

      const nextVersion = (existingVersions?.[0]?.version_number ?? 0) + 1;

      // Set all existing versions to not current
      await supabase
        .from('site_sketch_versions')
        .update({ is_current: false })
        .eq('location_id', locationId);

      // Create version record
      await supabase
        .from('site_sketch_versions')
        .insert({
          location_id: locationId,
          sketch_url: publicUrl,
          version_number: nextVersion,
          uploaded_by: user?.id,
          is_current: true,
        });

      // Update the location
      const { error: updateError } = await supabase
        .from('charging_locations')
        .update({ site_sketch_url: publicUrl })
        .eq('id', locationId);

      if (updateError) throw updateError;

      onUpdate(publicUrl);
      queryClient.invalidateQueries({ queryKey: ['site-sketch-versions', locationId] });

      toast({
        title: 'Schets geüpload',
        description: `Versie ${nextVersion} opgeslagen`,
      });
    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload mislukt',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = async () => {
    try {
      const { error } = await supabase
        .from('charging_locations')
        .update({ site_sketch_url: null })
        .eq('id', locationId);

      if (error) throw error;

      // Mark all versions as not current
      await supabase
        .from('site_sketch_versions')
        .update({ is_current: false })
        .eq('location_id', locationId);

      onUpdate(null);

      toast({
        title: 'Schets verwijderd',
        description: 'De schets is verwijderd (versies blijven beschikbaar)',
      });
    } catch (error: any) {
      toast({
        title: 'Verwijderen mislukt',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  }, [locationId]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  };

  return (
    <>
      <Card className={`flex flex-col ${className || 'h-[280px]'}`}>
        <CardHeader className="py-3 px-4 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm flex items-center gap-2">
            <Image className="w-4 h-4" />
            Situatieschets
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            onClick={() => setShowHistory(true)}
          >
            <History className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="flex-1 p-2 pt-0">
          {currentUrl ? (
            <div className="relative h-full group rounded-lg overflow-hidden">
              <img
                src={currentUrl}
                alt="Situatieschets"
                className="w-full h-full object-cover rounded-lg cursor-pointer"
                onClick={() => setShowPreview(true)}
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowPreview(true)}
                >
                  <ZoomIn className="w-4 h-4 mr-1" />
                  Bekijk
                </Button>
                <label>
                  <Button variant="secondary" size="sm" asChild>
                    <span>
                      <Upload className="w-4 h-4 mr-1" />
                      Vervang
                    </span>
                  </Button>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </label>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleRemove}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ) : (
            <label
              className={`flex flex-col items-center justify-center h-full rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                isDragOver
                  ? 'border-primary bg-primary/10'
                  : 'border-muted-foreground/25 hover:border-muted-foreground/50'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              {isUploading ? (
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              ) : (
                <>
                  <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                  <span className="text-sm text-muted-foreground text-center px-4">
                    Sleep een afbeelding hierheen of klik om te uploaden
                  </span>
                  <span className="text-xs text-muted-foreground mt-1">
                    PNG, JPEG, WebP (max 10MB)
                  </span>
                </>
              )}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={handleFileSelect}
              />
            </label>
          )}
        </CardContent>
      </Card>

      <SiteSketchVersionHistory
        locationId={locationId}
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        onRestore={(url) => onUpdate(url)}
      />

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Situatieschets</DialogTitle>
          </DialogHeader>
          {currentUrl && (
            <div className="flex items-center justify-center">
              <img
                src={currentUrl}
                alt="Situatieschets"
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
